import { ErrorMessage, Formik, useFormikContext } from 'formik';
import { useRef } from 'react';

import { Button } from '@src/design-system/button';
import {
  DialogBody,
  DialogFooter,
  DialogSubClose,
  DialogSubHeader,
} from '@src/design-system/dialog';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { Input } from '@src/design-system/input';
import { Label } from '@src/design-system/label';

import { FillUserIcon } from '@shared/assets/icons/shared/fill-user-icon';
import { FillEnvelopeIcon } from '@shared/assets/icons/shared/fill-envelope-icon';
import { FillBriefcaseIcon } from '@shared/assets/icons/shared/fill-briefcase-icon';
import { FillCompanyIcon } from '@shared/assets/icons/shared/fill-company-icon';
import {
  Upload,
  UploadArea,
  UploadButton,
  UploadError,
  UploadInputDisplay,
  UploadTrigger,
} from '@src/design-system/upload';
import {
  createInterviewerFormInitialValue,
  interviewerFormSchema,
  MAX_INTERVIEWER_IMAGE_SIZE,
  type TInterviewerValues,
} from '@shared/forms/interviewer';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@src/design-system/tabs';
import { cn } from '@src/design-system/helpers';
import AddingMemberAsInterviewer from './adding-member-as-interviewer';
import { INTERVIEWER_TYPES } from '@shared/types/interviewer';
import { formatTextForDisplay } from '@shared/utils/helpers/format-text-for-display';
import interviewerApi from '@src/features/participants/services/interviewer-api';

const ADD_INTERVIEWER_FORM = [
  {
    label: 'Interviewer Name',
    name: 'interviewerName',
    placeholder: 'ex: Saeed Gamal',
    prefixIcon: FillUserIcon,
    component: Input,
  },

  {
    inputType: 'email',
    label: 'Email',
    name: 'email',
    placeholder: 'ex: example@gmail.com',
    prefixIcon: FillEnvelopeIcon,
    component: Input,
  },
  {
    label: 'Job Title',
    name: 'jobTitle',
    placeholder: 'ex: Product designer',
    prefixIcon: FillBriefcaseIcon,
    component: Input,
  },

  {
    label: 'Company',
    name: 'companyName',
    placeholder: 'ex: Microsoft',
    prefixIcon: FillCompanyIcon,
    component: Input,
  },
] as const;

export function CreateInterviewerSubModal() {
  const { mutate: createInterviewer, isPending } = interviewerApi.useCreate();
  const { setFieldValue } = useFormikContext();

  const closeRef = useRef<HTMLDivElement>(null);

  const createInterviewerFromForm = (values: TInterviewerValues) => {
    createInterviewer(
      {
        company_name: values.companyName,
        job_title: values.jobTitle,
        email: values.email,
        name: values.interviewerName,
        ...(values.avatar ? { avatar: values.avatar } : {}),
      },
      {
        onSuccess: (data) => {
          closeRef.current?.click();
          setFieldValue('host', data);
        },
      }
    );
  };

  return (
    <div>
      <DialogSubHeader>Add Interviewer</DialogSubHeader>
      <Tabs defaultValue={INTERVIEWER_TYPES.external}>
        <Formik
          initialValues={createInterviewerFormInitialValue()}
          validationSchema={interviewerFormSchema}
          onSubmit={createInterviewerFromForm}
        >
          {(subFormik) => (
            <>
              <div className='flex gap-2'>
                <TabsList className='border-0 h-auto gap-2 p-3 pb-0'>
                  {Object.values(INTERVIEWER_TYPES).map((value) => (
                    <TabsTrigger
                      key={value}
                      value={value}
                      className={cn(
                        `px-4 py-2 border text-sm capitalize transition-none data-[state=active]:bg-primary-100 cursor-pointer data-[state=active]:border data-[state=active]:border-pale-border border-pale-border text-gray-600 rounded-full `
                      )}
                    >
                      {formatTextForDisplay(value)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              <TabsContent
                value={INTERVIEWER_TYPES.external}
                className='text-gray-500 mt-6'
              >
                <DialogBody className='pt-0 mt-0'>
                  {ADD_INTERVIEWER_FORM.map((field) => {
                    const FieldComponent = field.component;
                    const hasError =
                      !!subFormik.errors[field.name] &&
                      !!subFormik.touched[field.name];
                    return (
                      <div
                        key={field.name}
                        className='mb-6 last:mb-0'
                      >
                        <Label
                          htmlFor={field.name}
                          className='mb-2'
                        >
                          {field.label}
                        </Label>

                        <FieldComponent
                          prefixIcon={<field.prefixIcon />}
                          hasError={hasError}
                          id={field.name}
                          placeholder={field.placeholder}
                          onChange={(e) =>
                            subFormik.setFieldValue(field.name, e.target.value)
                          }
                          {...('inputType' in field && {
                            type: field.inputType,
                          })}
                        />

                        <ErrorMessage
                          name={field.name}
                          component={CustomErrorMessage}
                        />
                      </div>
                    );
                  })}
                  <Upload
                    maxSize={MAX_INTERVIEWER_IMAGE_SIZE}
                    onFileChange={(file) =>
                      subFormik.setFieldValue('avatar', file)
                    }
                  >
                    <UploadArea>
                      <Label>Interviewer Avatar (optional)</Label>
                      <UploadTrigger className='flex-1 flex h-[50px] w-full'>
                        <UploadButton />
                        <UploadInputDisplay />
                      </UploadTrigger>
                    </UploadArea>
                    <UploadError />
                  </Upload>
                </DialogBody>
                <DialogFooter>
                  <Button
                    onClick={() => subFormik.submitForm()}
                    isLoading={isPending}
                    className='flex-1'
                  >
                    Add
                  </Button>
                </DialogFooter>
                <DialogSubClose ref={closeRef} />
              </TabsContent>
            </>
          )}
        </Formik>

        <TabsContent
          value={INTERVIEWER_TYPES.member}
          className=' mt-0'
        >
          <AddingMemberAsInterviewer />
        </TabsContent>
      </Tabs>
    </div>
  );
}
